// Displays a report of tickets issued within a specified date range
// broken down by hour and date categories. The table headers are hours
// from 0 to 23, and the rows represent date range selected by the user.
import React from 'react';
import styles from '../css/inTicketsReport.module.css';
import { useRef } from 'react';
import { addHoursHeaders, getDatesInRange } from '../helpers/timeDifference.js';
import SimpleExcelExport from '../components/SimpleExportToExcel';

function InTicketsReport({ data, totalInTickets, start, end }) {
  const tableRef = useRef([]);
  const exportTableRef = useRef(null);

  return (
    <div className={styles.tableContainer}>
      <div className={styles.reportContentContainer}>
        <div className={styles.exportContainer}>
          <SimpleExcelExport data={data} targetTableRef={exportTableRef} reportName="InTicketsReport" />
        </div>
        {data.length > 0 && (
          <table ref={exportTableRef} className={styles.mainTable}>
            <caption>In Traffic Report</caption>
            <thead>
              <tr>
                <th>Date</th>
                {addHoursHeaders(tableRef.current).map((date, rowIndex) => (
                  <th key={rowIndex}>{date}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {getDatesInRange(start, end).map((date, i) => {
                const rowDate = new Date(date);
                return (
                  <tr key={i}>
                    <td key={i + 1}>{date}</td>
                    {tableRef.current.map((hour, hourIndex) => {
                      const match = data.find((item) => {
                        const d = new Date(item.DateIn);

                        // Match for the last hour (23)
                        return (
                          d.getFullYear() === rowDate.getFullYear() &&
                          d.getMonth() === rowDate.getMonth() &&
                          d.getDate() === rowDate.getDate() &&
                          Number(item.HourIn) === Number(hour)
                        );
                      });
                      if (hourIndex <= 23) {
                        return (
                          <>
                            <td key={hourIndex}>{match ? match.inTickets : 0}</td>
                          </>
                        );
                      } else {
                        return null;
                      }
                    })}
                    {data.map((totalRows, rowsIndex) => {
                      // Sum all tickets for the current date
                      const totalForDate = data
                        .filter((filterItem) => {
                          const d = new Date(filterItem.DateIn);
                          return (
                            d.getFullYear() === rowDate.getFullYear() &&
                            d.getMonth() === rowDate.getMonth() &&
                            d.getDate() === rowDate.getDate()
                          );
                        })
                        .reduce((acc, item) => Number(item.inTickets) + Number(acc), 0);
                      if (rowsIndex === 0) {
                        return (
                          <td
                            key={i + 1}
                            style={{
                              fontWeight: 'bold',
                            }}
                          >
                            {Number(totalForDate)}
                          </td>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </tr>
                );
              })}
              <tr>
                {/* Determine the total tickets per day */}
                <td
                  style={{
                    fontWeight: 'bold',
                  }}
                >
                  Total
                </td>
                {tableRef.current.map((hr, hrIndex) => {
                  const totalPerHour = data
                    .filter((filterItem) => Number(filterItem.HourIn) === Number(hr))
                    .reduce((hrSum, hrCurrentValue) => Number(hrSum) + Number(hrCurrentValue.inTickets), 0);
                  if (hrIndex <= 23) {
                    return (
                      <td
                        key={hrIndex}
                        style={{
                          fontWeight: 'bold',
                        }}
                      >
                        {totalPerHour || 0}
                      </td>
                    );
                  } else if (hrIndex === 24) {
                    return (
                      <td
                        key={hrIndex}
                        style={{
                          fontWeight: 'bold',
                        }}
                      >
                        {totalInTickets || 0}
                      </td>
                    );
                  } else {
                    return null;
                  }
                })}
              </tr>
              <tr>
                <td
                  style={{
                    fontWeight: 'bold',
                  }}
                >
                  Percentage
                </td>
                {tableRef.current.map((percentageHr, percentageIndex) => {
                  if (percentageIndex > 24) {
                    return null;
                  }
                  const percentPerHour = data
                    .filter((filterPercentItem) => Number(filterPercentItem.HourIn) === Number(percentageHr))
                    .reduce(
                      (percentSum, percentCurrentValue) =>
                        Number(percentSum) + (Number(percentCurrentValue.inTickets) / (totalInTickets || 0)) * 100,
                      0,
                    );

                  if (percentageIndex <= 23) {
                    return (
                      <td
                        key={percentageIndex}
                        style={{
                          fontWeight: 'bold',
                        }}
                      >
                        {percentPerHour.toFixed(1) + '%'}
                      </td>
                    );
                  } else {
                    return null;
                  }
                })}
                <td
                  style={{
                    fontWeight: 'bold',
                  }}
                >
                  100%
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default InTicketsReport;
